import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import {
  Box,
  Button,
  Grid,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  Typography,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { TFunction } from 'i18next';
import AddSiblingDialog from './AddSiblingDialog';
import EditSiblingDialog from './EditSiblingDialog';
import { SiblingFormData } from '../../../../models';
import { stringToSemanticDisplay } from '../../../../utils/DateUtils';
import ConfirmRemoveDialog from '../nonNationalExam/ConfirmRemoveDialog';
import { IOptionType } from '../../../../api/candidate/exam/model';

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  addButtonContainer: {
    margin: theme.spacing(1),
    display: 'inline-flex',
    cursor: 'pointer',
    '&:hover': {
      '& $icon, & $button': {
        color: theme.color.link.linkHover01,
      },
      '& $iconRemove, $buttonRemove': {
        color: theme.color.link.linkHover02,
      },
    },
  },
  button: {
    margin: 'auto',
    padding: 10,
    minWidth: 0,
  },
  icon: {
    margin: 'auto',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonFont: {
    '& span': {
      fontSize: '18px !important',
    },
    paddingBottom: theme.spacing(3),
  },
  buttonRemove: {
    color: theme.color.state.stateError,
  },
  headerTitleContainer: {
    marginBottom: theme.spacing(3),
  },
}));

const renderDobColumn = (data: SiblingFormData[], dataIndex: number) => (
  <Typography variant="body2">
    {stringToSemanticDisplay(
      data[dataIndex] && data[dataIndex].dateOfBirth ? data[dataIndex].dateOfBirth : ''
    )}
  </Typography>
);

const columnsNoAction = (t: TFunction, data: SiblingFormData[]) => {
  const columns: MUIDataTableColumnDef[] = [
    {
      label: t('exam->>NonNationalExam.Name'),
      name: 'name',
      options: {
        setCellProps: () => ({ style: { width: '35%' } }),
      },
    },
    {
      label: t('exam->>NonNationalExam.Gender'),
      name: 'gender',
      options: {
        setCellProps: () => ({ style: { width: '15%' } }),
      },
    },
    {
      label: t('exam->>NonNationalExam.DateOfBirth'),
      name: 'dateOfBirth',
      options: {
        setCellProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex: number) => renderDobColumn(data, dataIndex),
      },
    },
    {
      label: t('exam->>NonNationalExam.Level'),
      name: 'level',
      options: {
        setCellProps: () => ({ style: { width: '15%' } }),
      },
    },
  ];
  return columns;
};

const columnsAction = (
  t: TFunction,
  classes: Record<string,string>,
  theme: AugmentedTheme,
  data: SiblingFormData[],
  editSchoolClick: (dataIndex: number) => () => void,
  deleteSiblingClick: (dataIndex: number) => () => void
) => {
  const columns: MUIDataTableColumnDef[] = [
    {
      label: t('exam->>NonNationalExam.Name'),
      name: 'name',
      options: {
        setCellProps: () => ({ style: { width: '35%' } }),
      },
    },
    {
      label: t('exam->>NonNationalExam.Gender'),
      name: 'gender',
      options: {
        setCellProps: () => ({ style: { width: '15%' } }),
      },
    },
    {
      label: t('exam->>NonNationalExam.DateOfBirth'),
      name: 'dateOfBirth',
      options: {
        setCellProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex: number) => renderDobColumn(data, dataIndex),
      },
    },
    {
      label: t('exam->>NonNationalExam.Level'),
      name: 'level',
      options: {
        setCellProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex: number) => {
          if (data[dataIndex] && data[dataIndex].level) {
            const { level } = data[dataIndex];
            if (typeof level !== 'string') {
              return level.description;
            }
            return '-';
          }
          return '-';
        },
      },
    },
    {
      name: 'action',
      label: t('exam->>NonNationalExam.Action'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '20%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Grid container spacing={2}>
              <Grid className={classes.buttonContainer}>
                <Icon size="M" name={IconName.edit} color={theme.color.icon.iconSelected} />
                <Button
                  id="button-accessArrangement-edit"
                  className={`${classes.button} ${classes.buttonFont}`}
                  onClick={editSchoolClick(dataIndex)}
                  variant="text"
                  color="primary"
                  size="small"
                >
                  {t('accessArrangement->>AccessArrangementRequest.Button.Edit').toString()}
                </Button>
              </Grid>
              <Grid className={classes.buttonContainer}>
                <Icon size="M" name={IconName.trash} color={theme.color.icon.iconTrash} />
                <Button
                  id="button-accessArrangement-edit"
                  className={`${classes.button} ${classes.buttonFont} ${classes.buttonRemove}`}
                  onClick={deleteSiblingClick(dataIndex)}
                  variant="text"
                  size="small"
                >
                  {t('exam->>ExamSchoolingDetails.Remove').toString()}
                </Button>
              </Grid>
            </Grid>
          </>
        ),
      },
    },
  ];
  return columns;
};

interface SiblingInfoTableProps {
  isShowAction?: boolean;
  data: SiblingFormData[];
  levels?: IOptionType[];
  setSiblings?: Dispatch<SetStateAction<SiblingFormData[]>>;
  isDisableAdd?: boolean;
  hasEditAccess?: boolean;
}

const SiblingInfoTable = ({
  isShowAction = true,
  data,
  levels,
  setSiblings,
  isDisableAdd = false,
  hasEditAccess = false,
}: SiblingInfoTableProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();

  const [addDialogState, setAddDialogState] = useState<boolean>(false);
  const [editDialogState, setEditDialogState] = useState<boolean>(false);
  const [removeDialogState, setRemoveDialogState] = useState<boolean>(false);
  const [siblingSelected, setSiblingSelected] = useState<SiblingFormData>();
  const [indexSelected, setIndexSelected] = useState<number>(0);

  const addSchoolClick = () => {
    setAddDialogState(true);
  };

  const onAddDialogClose = () => {
    setAddDialogState(false);
  };

  const editSchoolClick = useCallback(
    (dataIndex: number) => () => {
      setIndexSelected(dataIndex);
      setSiblingSelected(data[dataIndex]);
      setEditDialogState(true);
    },
    [data]
  );

  const onUpdateDataTable = (updateValue: SiblingFormData) => {
    const newData = [...data];
    newData[indexSelected] = { ...updateValue };
    if (setSiblings) {
      setSiblings([...newData]);
    }
  };

  const onEditDialogClose = () => {
    setEditDialogState(false);
  };

  const deleteSiblingClick = useCallback(
    (dataIndex: number) => () => {
      setIndexSelected(dataIndex);
      setSiblingSelected(data[dataIndex]);
      setRemoveDialogState(true);
    },
    [data]
  );

  const onDeleteSibling = () => {
    const newData = [...data];
    newData.splice(indexSelected, 1);
    if (setSiblings) {
      setSiblings([...newData]);
    }
    setRemoveDialogState(false);
  };

  const columns: MUIDataTableColumnDef[] = useMemo(() => {
    if (!isShowAction) {
      return columnsNoAction(t, data);
    }
    return columnsAction(t, classes, theme, data, editSchoolClick, deleteSiblingClick);
  }, [classes, data, deleteSiblingClick, editSchoolClick, isShowAction, t, theme]);

  return (
    <Grid sm={12}>
      <Grid
        container
        direction="row"
        className={`${classes.headerContainer} ${isShowAction ? '' : classes.headerTitleContainer}`}
      >
        {isShowAction && (
          <Typography variant="h3">{t('exam->>NonNationalExam.SiblingInfo')}</Typography>
        )}
        {isShowAction && (
          <Box className={classes.addButtonContainer}>
            <Icon
              size="M"
              className={classes.icon}
              name={IconName.add}
              color={
                isDisableAdd || !hasEditAccess
                  ? theme.color.link.linkDisabledBg01
                  : theme.color.icon.iconSelected
              }
            />
            <Button
              id="button-add-sibling"
              className={classes.button}
              onClick={addSchoolClick}
              variant="text"
              color="primary"
              disabled={isDisableAdd || !hasEditAccess}
            >
              {t('exam->>NonNationalExam.AddSibling')}
            </Button>
          </Box>
        )}
      </Grid>
      <MUIDataTable
        columns={columns}
        data={data}
        emptyDataLabel={t('exam->>NonNationalExam.SchoolEmpty')}
      />
      <AddSiblingDialog
        modalState={addDialogState}
        onDialogClose={onAddDialogClose}
        levels={levels}
        setSiblings={setSiblings}
      />
      <EditSiblingDialog
        modalState={editDialogState}
        onDialogClose={onEditDialogClose}
        levels={levels}
        onUpdateDataTable={onUpdateDataTable}
        siblingSelected={siblingSelected}
      />
      <ConfirmRemoveDialog
        contentText={t('exam->>ExamSchoolingDetails.RemoveSiblingContent')}
        modalState={removeDialogState}
        onDialogClose={() => setRemoveDialogState(false)}
        proceedClick={onDeleteSibling}
        title={`${t('exam->>ExamSchoolingDetails.Remove')} ${siblingSelected?.name}${t(
          'exam->>ExamSchoolingDetails.FromSiblingInfo'
        )}`}
      />
    </Grid>
  );
};

export default SiblingInfoTable;
